/**
 * A small agentic harness that runs in the browser.
 *
 * The loop is the whole thing: send the conversation plus the tool schemas to
 * the model; if it asks for tools, run them locally and hand back the results;
 * repeat until it answers in prose or hits the step ceiling.
 *
 * Architecture is borrowed from DeepSeek Harness's plugin model — capabilities
 * are registered, not hardcoded into the loop — but nothing of it runs here:
 * `dsh` is a Node server and this has to work on a static site. So the loop is
 * ours, the model call goes to the site's existing /api/chat (Groq), and every
 * tool executes client-side against data the page already has.
 *
 * The harness emits a step for everything it does. That trace is meant to be
 * shown, not hidden: an agent whose reasoning you cannot inspect is a magic
 * trick, and a magic trick is not a credential.
 */

import { ToolContext, findTool, toolSchemas } from './tools';
import { runContextIR, compressHistory, ContextIR } from './contextIR';
import { runMultiPath } from './multiPath';

export type StepKind = 'thinking' | 'tool_call' | 'tool_result' | 'answer' | 'error' | 'context_ir' | 'multi_path';

export interface Step {
    kind: StepKind;
    /** One line, written for a human reading the transcript */
    text: string;
    tool?: string;
    /** Raw payload, for the expandable detail view */
    detail?: unknown;
}

export interface ChatMessage {
    role: 'system' | 'user' | 'assistant' | 'tool';
    content: string;
    tool_calls?: ToolCall[];
    tool_call_id?: string;
    name?: string;
}

interface ToolCall {
    id: string;
    type: 'function';
    function: { name: string; arguments: string };
}

export interface HarnessOptions {
    model: string;
    pageTitle?: string;
    pageContent?: string;
    /** Hard ceiling on model round-trips. Prevents a tool-call loop billing forever. */
    maxSteps?: number;
    /** Carried through to the API so server-side logs stay grouped by session. */
    sessionId?: string;
    signal?: AbortSignal;
    /** Skip the Context-IR preprocessing pass to save token quota */
    skipContextIR?: boolean;
    /** Use multi-path (sampler + evolved arms) instead of single LLM call */
    multiPath?: boolean;
}

export interface HarnessResult {
    answer: string;
    steps: Step[];
    /** The full message list, to carry into the next turn */
    messages: ChatMessage[];
    /** The context-ir output from this turn, if available */
    contextIR?: ContextIR;
}

const DEFAULT_MAX_STEPS = 5;

async function callModel(
    messages: ChatMessage[],
    opts: HarnessOptions,
): Promise<{ reply: string; tool_calls?: ToolCall[] }> {
    const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            messages,
            model: opts.model,
            page_title: opts.pageTitle,
            page_content: opts.pageContent,
            session_id: opts.sessionId,
            tools: toolSchemas,
        }),
        signal: opts.signal,
    });

    if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`chat ${res.status}${body ? `: ${body.slice(0, 160)}` : ''}`);
    }
    return res.json();
}

/**
 * Run one turn to completion.
 *
 * `onStep` fires as work happens so the UI can narrate live rather than
 * revealing everything at the end.
 */
export async function runAgent(
    history: ChatMessage[],
    ctx: ToolContext,
    opts: HarnessOptions,
    onStep?: (step: Step) => void,
): Promise<HarnessResult> {
    const checkCancelled = () => {
        if (opts.signal?.aborted) throw new DOMException('Response stopped', 'AbortError');
    };
    checkCancelled();
    const messages: ChatMessage[] = [...history];
    const steps: Step[] = [];
    const maxSteps = opts.maxSteps ?? DEFAULT_MAX_STEPS;

    const emit = (s: Step) => {
        checkCancelled();
        steps.push(s);
        onStep?.(s);
    };

    // ── Context-IR pass (H3-inspired preprocessing stage) ────────────────────
    const userMessage = messages[messages.length - 1]?.content ?? '';

    let ir: ContextIR;
    if (opts.skipContextIR) {
        // Skip when token budget is tight (e.g. gpt-oss-120b 8k TPM limit)
        ir = { intent: userMessage, audience: 'unknown', urgency: 'normal',
               relevant_sections: [], tools_hint: [], enriched_prompt: userMessage,
               tone_hint: 'concise', _error: 'skipped' };
    } else {
        const historySummary = compressHistory(
            messages.slice(0, -1) as Array<{ role: string; content: string }>,
        );
        emit({ kind: 'context_ir', text: 'understanding intent…' });
        ir = await runContextIR(userMessage, opts.pageTitle, historySummary, opts.signal);
        emit({
            kind: 'context_ir',
            text: `intent: ${ir.intent} · audience: ${ir.audience}${ir._error ? ' (degraded)' : ''}`,
            detail: ir,
        });
    }

    // Replace the last user message with the enriched prompt so the LLM
    // benefits from the IR without changing the visible transcript.
    // Only swap if the enriched prompt is meaningfully different.
    const messagesWithIR: ChatMessage[] = [
        ...messages.slice(0, -1),
        {
            ...messages[messages.length - 1],
            content: ir.enriched_prompt !== userMessage && !ir._error
                ? ir.enriched_prompt
                : userMessage,
        },
    ];

    // Inject a brief audience/tone note as a system hint for this turn only
    const audienceHint = ir.audience !== 'unknown'
        ? `[Context-IR: visitor is a ${ir.audience}, tone: ${ir.tone_hint}]`
        : '';

    for (let i = 0; i < maxSteps; i++) {
        checkCancelled();
        emit({ kind: 'thinking', text: i === 0 ? 'thinking' : 'thinking again' });

        // ── Multi-path: run sampler + evolved arms, score both, pick winner ──
        // Only on the first turn (i===0) and when no tool calls are expected.
        // Mirrors AMOE evolve loop's two-arm structure and H3's regeneration.
        if (i === 0 && opts.multiPath) {
            try {
                const mp = await runMultiPath(
                    messagesWithIR,
                    userMessage,
                    {
                        model: opts.model,
                        pageTitle: opts.pageTitle,
                        pageContent: [opts.pageContent, audienceHint].filter(Boolean).join('\n'),
                        signal: opts.signal,
                        onStep: emit,
                    },
                );
                const answer = mp.answer.trim();
                messages.push({ role: 'assistant', content: answer });
                emit({ kind: 'answer', text: answer });
                return { answer, steps, messages, contextIR: ir };
            } catch {
                checkCancelled();
                // Multi-path failed — fall through to single LLM call
                emit({ kind: 'thinking', text: 'multi-path failed, falling back to single call' });
            }
        }

        const out = await callModel(
            messagesWithIR,
            {
                ...opts,
                pageContent: [opts.pageContent, audienceHint].filter(Boolean).join('\n'),
            },
        );

        // No tool calls: the model answered, and the turn is done.
        if (!out.tool_calls || out.tool_calls.length === 0) {
            const answer = (out.reply || '').trim();
            // Store the original user message in history (not the enriched one)
            messages.push({ role: 'assistant', content: answer });
            emit({ kind: 'answer', text: answer });
            return { answer, steps, messages, contextIR: ir };
        }

        // Record the assistant's tool-calling turn verbatim.
        messagesWithIR.push({
            role: 'assistant',
            content: out.reply || '',
            tool_calls: out.tool_calls,
        });
        messages.push({
            role: 'assistant',
            content: out.reply || '',
            tool_calls: out.tool_calls,
        });

        for (const call of out.tool_calls) {
            checkCancelled();
            const name = call.function?.name ?? '(unnamed)';
            let args: Record<string, any> = {};
            try {
                args = call.function?.arguments ? JSON.parse(call.function.arguments) : {};
            } catch {
                emit({ kind: 'error', text: `${name}: unparseable arguments`, tool: name });
                const toolErr = {
                    role: 'tool' as const,
                    tool_call_id: call.id,
                    name,
                    content: JSON.stringify({ error: 'arguments were not valid JSON' }),
                };
                messages.push(toolErr);
                messagesWithIR.push(toolErr);
                continue;
            }

            emit({
                kind: 'tool_call',
                text: `${name}(${Object.entries(args)
                    .map(([k, v]) => `${k}: ${JSON.stringify(v)}`)
                    .join(', ')})`,
                tool: name,
                detail: args,
            });

            const tool = findTool(name);
            if (!tool) {
                emit({ kind: 'error', text: `no such tool: ${name}`, tool: name });
                const toolErr = {
                    role: 'tool' as const,
                    tool_call_id: call.id,
                    name,
                    content: JSON.stringify({ error: `unknown tool ${name}` }),
                };
                messages.push(toolErr);
                messagesWithIR.push(toolErr);
                continue;
            }

            try {
                const result = await tool.run(args, ctx);
                checkCancelled();
                emit({
                    kind: 'tool_result',
                    text: result.summary,
                    tool: name,
                    detail: result.data,
                });
                const toolResult = {
                    role: 'tool' as const,
                    tool_call_id: call.id,
                    name,
                    content: JSON.stringify(result.data).slice(0, 3000),
                };
                messages.push(toolResult);
                messagesWithIR.push(toolResult);
            } catch (err: any) {
                checkCancelled();
                const msg = err?.message ?? String(err);
                emit({ kind: 'error', text: `${name} failed: ${msg}`, tool: name });
                const toolErr = {
                    role: 'tool' as const,
                    tool_call_id: call.id,
                    name,
                    content: JSON.stringify({ error: msg }),
                };
                messages.push(toolErr);
                messagesWithIR.push(toolErr);
            }
        }
    }

    const text = 'I kept reaching for tools without settling on an answer — try narrowing the question.';
    emit({ kind: 'error', text });
    return { answer: text, steps, messages, contextIR: ir };
}
